'use client';
import { Drawer, Layout } from 'antd';
import AppSidebar from '../sidebar/AppSidebar';
import { useChatContext } from '../../contexts/ChatContext';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import RouteTracker from '../navigation/RouteTracker';
import { useInit } from '../../contexts/InitContext';
import GlobalSearchHeader from './GlobalSearchHeader';
import RightSidePanel from '../explore/RightSidePanel';
import { RightPanelProvider, useRightPanel } from '../../contexts/RightPanelContext';
import useViewportWidth from '../../hooks/useViewportWidth';
import { useEmbeddedAuth } from '../../contexts/EmbeddedAuthContext';
import { getToken } from '../../services/authService';
import sidebarDrawerToggleIcon from '../../assets/icons/SidebarDrawerToggle.svg';
import PanelControlButtons from './PanelControlButtons';

const { Content } = Layout;

const AppLayoutContent = ({ children, onSearch, defaultSearchValue, drawerMode, viewportWidth }) => {
  const { isPanelOpen, closePanel } = useRightPanel();
  const location = useLocation();
  const allowNarrowContent = location.pathname === '/explore' || location.pathname.startsWith('/explore/');
  const smallNoShrink = viewportWidth < 928 && !allowNarrowContent;

  return (
    <Content className="main-content">
      <div className={smallNoShrink ? 'overflow-x-auto' : undefined}>
        <div
          className={`w-full max-w-[1400px] mx-auto px-[var(--spacing-section)] pb-[var(--spacing-section)] ${
            smallNoShrink ? 'min-w-[928px]' : 'min-w-0'
          }`}
        >
          <div className="flex gap-6 items-start">
            <div className="flex-1 min-w-0">{children}</div>
            {!drawerMode && (
              <div className="w-[328px] h-[732px] shrink-0">
                <RightSidePanel variant="viewport" />
              </div>
            )}
          </div>
        </div>
      </div>

      {drawerMode && (
        <Drawer
          open={isPanelOpen}
          onClose={closePanel}
          placement="right"
          width={340}
          styles={{ body: { padding: 6 } }}
        >
          <RightSidePanel variant="drawer" onItemClick={closePanel} />
        </Drawer>
      )}
    </Content>
  );
};

const AppLayout = ({ children }) => {
  const { isChatOpen } = useChatContext();
  const { isInitialized, isInitializing } = useInit();
  const { showLoginModal } = useEmbeddedAuth();
  // 固定使用large尺寸，不再根据屏幕宽度动态调整
  const [screenSize] = useState('large');
  const location = useLocation();
  const navigate = useNavigate()
  const viewportWidth = useViewportWidth();
  const drawerMode = viewportWidth < 1288;
  const sidebarDrawerMode = viewportWidth < 1340;
  const headerCompactMode = viewportWidth < 928;
  const [isSidebarDrawerOpen, setIsSidebarDrawerOpen] = useState(false);
  const fixedSpacing = 24;
  const sidebarDrawerButtonSize = 36;
  const sidebarDrawerButtonGap = 12;
  const fixedLogoWidth = 74.06;
  const fixedLogoLeft = sidebarDrawerMode
    ? fixedSpacing + sidebarDrawerButtonSize + sidebarDrawerButtonGap
    : fixedSpacing;
  const drawerHeaderPaddingLeft = fixedLogoLeft + fixedLogoWidth + fixedSpacing;
  const headerRightControlsWidth = 186;
  const headerRightControlsGap = 12;
  const drawerHeaderPaddingRight = headerCompactMode
    ? fixedSpacing + headerRightControlsWidth + headerRightControlsGap
    : fixedSpacing;

  useEffect(() => {
    if (!sidebarDrawerMode && isSidebarDrawerOpen) {
      setIsSidebarDrawerOpen(false);
    }
  }, [sidebarDrawerMode, isSidebarDrawerOpen]);

  useEffect(() => {
    if (sidebarDrawerMode && isSidebarDrawerOpen) {
      setIsSidebarDrawerOpen(false);
    }
  }, [location.pathname]);

  // 获取搜索框占位符
  const getSearchPlaceholder = () => {
    switch (location.pathname) {
      case '/explore/friends':
        return '搜索朋友帖子...';
      case '/explore/proposals':
        return '搜索提案...';
      case '/explore/ideas':
        return '搜索创意想法...';
      case '/tasks':
        return '搜索提案...';
      case '/explore':
        return '搜索探索内容...';
      default:
        return '搜索感兴趣的内容...';
    }
  };

  // 处理搜索逻辑
  const handleSearch = (searchValue) => {
    if (!getToken()) {
      showLoginModal();
      return;
    }

    const trimmedValue = searchValue.trim();
    if (!trimmedValue) {
      navigate('/search', { replace: true });
      return;
    }

    const params = new URLSearchParams();
    params.set('search', trimmedValue);
    params.set('tab', 'proposal');
    navigate(`/search?${params.toString()}`, { replace: true });
  };

  // 显示初始化状态（可选）
  useEffect(() => {
    if (isInitializing) {
      console.log('系统正在初始化数据...');
    } else if (isInitialized) {
      console.log('系统初始化完成');
    }
  }, [isInitializing, isInitialized]);

  return (
    <div className="app-container">
      {/* 路由跟踪器 - 监听所有路由变化 */}
      <RouteTracker />
      {sidebarDrawerMode && (
        <button
          type="button"
          aria-label="打开侧边栏"
          className="fixed top-[24px] left-[24px] z-50 w-9 h-9"
          onClick={() => setIsSidebarDrawerOpen((v) => !v)}
        >
          <img alt="" className="w-9 h-9" src={sidebarDrawerToggleIcon} />
        </button>
      )}
      <Link to="/" className="fixed top-[24px] z-50" style={{ left: fixedLogoLeft }}>
        <img
          alt="logo"
          className="w-[74.06px] h-[36px]"
          src="/logo-协创者.svg"
        />
      </Link>

      <Layout className={`app-layout screen-${screenSize}`}>
        <RightPanelProvider mode={drawerMode ? 'drawer' : 'sidebar'}>
          {headerCompactMode && (
            <div className="fixed top-[24px] right-[24px] z-50" data-testid="header-right-controls">
              <PanelControlButtons />
            </div>
          )}
          <div className="bg-[var(--color-bg-base)]">
            <div
              className={`w-full max-w-[1400px] mx-auto pt-[var(--spacing-section)] min-w-0 ${
                drawerMode ? 'pr-[var(--spacing-section)]' : 'px-[var(--spacing-section)]'
              }`}
              data-testid="header-wrap"
              style={
                drawerMode
                  ? {
                      paddingLeft: drawerHeaderPaddingLeft,
                      paddingRight: drawerHeaderPaddingRight,
                    }
                  : undefined
              }
            >
              <GlobalSearchHeader
                onSearch={handleSearch}
                defaultSearchValue={new URLSearchParams(location.search).get('search') || ''}
                drawerMode={drawerMode}
                placeholder="搜索探索内容..."
                showRightControls={!headerCompactMode}
              />
            </div>
          </div>

          <Layout className="app-body-layout">
            {!sidebarDrawerMode && <AppSidebar />}

            <Layout className="main-content-layout">
              <div className="content-with-chat-layout">
                <div
                  className={`content-layout ${
                    isChatOpen ? 'chat-open' : 'chat-closed'
                  }`}
                  style={{ width: isChatOpen ? 'calc(100% - 380px)' : '100%' }}
                >
                  <AppLayoutContent
                    onSearch={handleSearch}
                    defaultSearchValue={new URLSearchParams(location.search).get('search') || ''}
                    drawerMode={drawerMode}
                    viewportWidth={viewportWidth}
                  >
                    {children}
                  </AppLayoutContent>
                </div>
              </div>
            </Layout>
          </Layout>
          {sidebarDrawerMode && (
            <Drawer
              open={isSidebarDrawerOpen}
              onClose={() => setIsSidebarDrawerOpen(false)}
              placement="left"
              width={168}
              closable={false}
              styles={{ body: { padding: 0 } }}
            >
              <AppSidebar variant="drawer" />
            </Drawer>
          )}
        </RightPanelProvider>
      </Layout>
    </div>
  );
};

export default AppLayout;
